// @flow
import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { SettingsMenu, PlatformSwitch, AppMobileHeader, AppMobileFooter, EmptyState } from '@blocks';
import { Button, ClickableList, DoctorColorIcon, Spinner } from '@elements';
import type { User, BundledPermissions } from '@types';
import { useTranslationNs } from '@helpers/helpers';
import './TeamList.scss';

export type TeamMember = {
  userId: number,
  roleType: 'Doctor' | 'Secretary',
  roleId: number,
  name: string,
  nameWithTitle: string,
  email: string,
  profession: string,
  color: ?number,
  manager: boolean,
  owner: boolean,
};

export type Team = {
  canInviteDoctor: boolean,
  canInviteSecretary: boolean,
  limitDoctors: ?number,
  dealSummary: ?string,
  members: Array<TeamMember>,
};

type Props = {
  user: User,
  permissions: BundledPermissions,
  onLogout: Function,
  // null until it loads (or when the user isn't a manager)
  team: ?Team,
  isLoading: boolean,
  failed: boolean,
  isManager: boolean,
  // An iOS build in App Store review: no plan details nor upgrade prompts
  storeReview?: boolean,
};

const memberRole = (member: TeamMember, _: Function) => {
  if (member.owner) return <span className="teamBadge owner">{_('label/team/owner')}</span>;
  if (member.manager) return <span className="teamBadge manager">{_('label/team/manager')}</span>;
  return null;
};

// Ajustes › Equipe: the clinic's professionals and secretaries, who owns the clinic and who manages it, and what the
// owner's plan allows (the Painel de Controle's "Gerenciar Equipe"). Managers only: the API answers 403 to the others.
const TeamList = ({
  user, permissions, onLogout, team, isLoading, failed, isManager, storeReview,
}: Props) => {
  const [_] = useTranslationNs('settings');
  // _ has no interpolation
  const { t } = useTranslation();

  const members = team ? team.members : [];
  const doctors = members.filter(member => member.roleType === 'Doctor');
  const secretaries = members.filter(member => member.roleType === 'Secretary');

  const doctorItems = doctors.map(member => ({
    linkTo: `/ajustes/doutores/${member.roleId}`,
    customRendererData: { member },
  }));
  const secretaryItems = secretaries.map(member => ({
    linkTo: `/ajustes/secretarias/${member.roleId}`,
    customRendererData: { member },
  }));

  const renderMember = ({ member }) => [
    <div className="teamMember" key="member" data-testid={`team-member-${member.userId}`}>
      {member.roleType === 'Doctor' && <span className="teamDot"><DoctorColorIcon colorId={member.color} /></span>}
      <div className="teamMemberText">
        <span className="teamMemberName">{member.roleType === 'Doctor' ? member.nameWithTitle : member.name}</span>
        <span className="teamMemberDetails">
          {member.roleType === 'Doctor' && member.profession ? `${member.profession} · ${member.email}` : member.email}
        </span>
      </div>
    </div>,
    <span key="role" className="teamRole">{memberRole(member, _)}</span>,
  ];

  const countLabel = (key: string, count: number) => t(`settings:label/team/${key}_count_${count === 1 ? 'one' : 'other'}`, { count });

  const limitNotice = () => {
    if (!team || team.limitDoctors == null || storeReview) return null;
    const atLimit = doctors.length >= team.limitDoctors;
    return (
      <p className={classnames('teamNotice', { warning: atLimit })} role="status">
        {t(`settings:label/team/${atLimit ? 'limit_reached' : 'limit'}`, { limit: team.limitDoctors, count: doctors.length })}
      </p>
    );
  };

  const section = ({
    title, count, inviteText, inviteTo, canInvite, lockedHint, items, empty, testId,
  }) => (
    <section className="teamSection" data-testid={testId}>
      <div className="toolbar dashed">
        <h2>{`${title} (${count})`}</h2>
        <Button className="primary" text={inviteText} linkTo={canInvite ? inviteTo : undefined} disabled={!canInvite} />
      </div>
      {!canInvite && <p className="teamLockedHint">{lockedHint}</p>}
      {items.length > 0 ? <ClickableList items={items} customRenderer={renderMember} card /> : empty}
    </section>
  );

  const content = () => {
    if (!isManager) return <EmptyState padding={50} title={_('label/team/managers_only')} />;
    if (!team) {
      return failed && !isLoading
        ? <EmptyState padding={50} title={_('label/team/load_error')} />
        : <Spinner text={_('label/loading', 'global')} />;
    }

    return (
      <React.Fragment>
        {team.dealSummary && !storeReview && (
          <div className="teamNotice">
            <strong>{_('label/team/deal_summary')}</strong>
            <p className="teamDealSummary">{team.dealSummary}</p>
          </div>
        )}
        {limitNotice()}
        {section({
          testId: 'team-doctors',
          title: _('label/team/doctors'),
          count: doctors.length,
          inviteText: _('label/team/invite_doctor'),
          inviteTo: '/ajustes/doutores/novo',
          canInvite: team.canInviteDoctor,
          lockedHint: _(storeReview ? 'label/team/doctor_limit_reached_review' : 'label/team/doctor_limit_reached'),
          items: doctorItems,
          empty: <EmptyState padding={30} title={_('label/team/no_doctors')} />,
        })}
        {section({
          testId: 'team-secretaries',
          title: _('label/team/secretaries'),
          count: secretaries.length,
          inviteText: _('label/team/invite_secretary'),
          inviteTo: '/ajustes/secretarias/nova',
          canInvite: team.canInviteSecretary,
          lockedHint: _(storeReview ? 'label/team/secretary_limit_reached_review' : 'label/team/secretary_limit_reached'),
          items: secretaryItems,
          empty: (
            <EmptyState
              padding={30}
              title={_('label/secretaries/list/empty')}
              hint={() => (
                <React.Fragment>
                  {_('label/secretaries/list/hint_1')}
                  <br />
                  {_('label/secretaries/list/hint_2')}
                </React.Fragment>
              )}
            />
          ),
        })}
        <div className="teamSupport">
          <span>{_('label/team/support')}</span>
          <Button className="linkButton" text={_('label/team/support_action')} intercom />
        </div>
      </React.Fragment>
    );
  };

  const summary = team && isManager
    ? t('settings:label/team/summary', {
      doctors: countLabel('doctors', doctors.length),
      secretaries: countLabel('secretaries', secretaries.length),
    })
    : null;

  const pageContent = (
    <div className="teamPage">
      <div className="toolbar dashed teamHeader">
        <h1>{_('label/team/title')}</h1>
        {summary && <span className="teamSummary">{summary}</span>}
      </div>
      {content()}
    </div>
  );

  return (
    <PlatformSwitch
      inject
      desktop={() => (
        <div id="teamPage" className="appContent settingsPage">
          <aside className="sidebar">
            <SettingsMenu user={user} onLogout={onLogout} permissions={permissions} />
          </aside>
          <section className="mainSection">
            <div className="centerContent">{pageContent}</div>
          </section>
        </div>
      )}
      mobile={() => (
        <React.Fragment>
          <AppMobileHeader title={_('label/team/title')} backLinkTo="/ajustes" />
          <div id="teamPage" className="appContent settingsPage">
            <section className="mainSection">
              <div className="centerContent">{pageContent}</div>
            </section>
          </div>
          <AppMobileFooter />
        </React.Fragment>
      )}
    />
  );
};

export default TeamList;
